import { useEffect, useRef, useSyncExternalStore } from "react";

import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import * as Sentry from "@sentry/browser";
import { CircularProgress, Heading } from "@stacklet/ui";
import { signInWithRedirect, signOut } from "aws-amplify/auth";
import { useDeepCompareEffect } from "use-deep-compare";

import { AuthClaimsProvider } from "app/contexts/AuthClaimsContext";
import { clearLocalIdpUser } from "app/utils/auth";
import { localDevEnabled } from "app/utils/localDev";
import {
  clearOAuthCallback,
  getOAuthCallbackStatus,
  subscribeOAuthCallback,
} from "app/utils/oauthCallback";
import { storeRedirectReturnCookie } from "app/utils/redirection";
import StackletLogo from "assets/images/logo_vertical_color_1.svg?react";

import { ForceNewPasswordScreen } from "./ForceNewPasswordScreen";
import { LocalIdpLogin } from "./LocalIdpLogin";
import { ResetPasswordScreen } from "./ResetPasswordScreen";
import { SignInScreen } from "./SignInScreen";
import "./Login.css";
import type { UseAuthenticator } from "@aws-amplify/ui-react";

type AuthRoute = UseAuthenticator["route"];

/**
 * Sends the browser to Cognito's hosted UI for the SAML provider registered
 * under `idpId` (platform registers each provider's `idp_id` as its Cognito
 * IdP identifier). Must go through Amplify: v6 only completes a redirect it
 * started itself, by matching the `state` it stored on the way out. The
 * domain, scopes and redirect URI come from the config in bootstrap.
 */
export function fedSignIn(idpId: string): Promise<void> {
  return signInWithRedirect({ provider: { idpIdentifier: idpId } });
}

/**
 * Routes still rendered by Amplify's own UI. Mounting `<Authenticator>` on the
 * others would add a second, invisible username/password form.
 */
const AMPLIFY_ROUTES = new Set<AuthRoute>([
  "confirmSignIn", // MFA challenge
  "confirmSignUp", // unconfirmed user redirected off sign-in
  "confirmVerifyUser",
  "forceNewPassword",
  "selectMfaType",
  "setupEmail",
  "setupTotp",
  "signUp", // transient on the way to confirmSignUp
  "verifyUser",
]);

/**
 * Amplify's fields for this route are the two password ones plus the pool's
 * required attributes, so a third means it wants profile data our screen
 * doesn't collect — hand those pools back to Amplify.
 */
function ownsForceNewPassword(fields: { name?: string }[]): boolean {
  return fields.length <= 2;
}

function CenteredLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-overlay-medium">
      <CircularProgress size="xl" />
    </div>
  );
}

// Header for Amplify's remaining screens (MFA, TOTP setup). Our own screens
// don't use it.
const authenticatorComponents = {
  Header() {
    return (
      <div className="mb-2xl flex flex-col items-center justify-center gap-xs">
        <StackletLogo className="stacklet-logo" height="64" width="64" />
        <Heading className="font-normal" level={2}>
          Sign In
        </Heading>
      </div>
    );
  },
};

/**
 * Sends the machine's `INIT`, which it waits for in `setup`. Amplify normally
 * does this inside `<Authenticator>`, which we no longer always mount
 */
function useInitAuthMachine(
  route: AuthRoute,
  initializeMachine: UseAuthenticator["initializeMachine"],
) {
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && route === "setup" && !localDevEnabled()) {
      initializeMachine({});
      hasInitialized.current = true;
    }
  });
}

/** Props for the Login component */
interface Props {
  /** Children to render when authenticated */
  children: React.ReactNode;
}

export function Login({ children }: Props) {
  const { route, error, fields, initializeMachine } = useAuthenticator(
    (context) => [context.route, context.error],
  );
  const oauthStatus = useSyncExternalStore(
    subscribeOAuthCallback,
    getOAuthCallbackStatus,
  );
  const searchParams = new URLSearchParams(window.location.search);

  useInitAuthMachine(route, initializeMachine);

  // Signing out doesn't always reload (an expired refresh token drops straight
  // back to `signIn`), and a stale `pending` would strand that user on the loader.
  useEffect(() => {
    if (route === "authenticated") {
      clearOAuthCallback();
    }
  }, [route]);

  useDeepCompareEffect(() => {
    if (error) {
      const errorMessage =
        typeof error === "string" ? error : "An authentication error occurred";
      console.error("Authentication error:", error);
      Sentry.captureException(
        new Error(`Authentication error: ${errorMessage}`),
      );
    }
  }, [error]);

  // Local-development backends (platform's `just local-console`,
  // VITE_DISABLE_AUTH=true): there's no Cognito, so swap the Amplify sign-in
  // for a trivial username form whose choice drives the local IdP (see
  // LocalIdpLogin / getAuthToken).  The DEV guard keeps this out of prod
  // builds even if the env var leaks into one.
  if (localDevEnabled()) {
    if (searchParams.has("logout")) {
      clearLocalIdpUser();
      window.location.assign(window.location.origin);
      return null;
    }
    return <LocalIdpLogin>{children}</LocalIdpLogin>;
  }

  if (searchParams.has("logout")) {
    signOut().then(() => window.location.assign(window.location.origin));
    return null;
  }

  if (route === "authenticated") {
    return <AuthClaimsProvider>{children}</AuthClaimsProvider>;
  }

  if (route === "forceNewPassword" && ownsForceNewPassword(fields)) {
    return <ForceNewPasswordScreen />;
  }

  if (AMPLIFY_ROUTES.has(route)) {
    return (
      <Authenticator components={authenticatorComponents} hideSignUp>
        {children}
      </Authenticator>
    );
  }

  if (route === "forgotPassword" || route === "confirmResetPassword") {
    return <ResetPasswordScreen />;
  }

  if (route !== "signIn" || (oauthStatus === "pending" && !error)) {
    return <CenteredLoader />;
  }

  storeRedirectReturnCookie(); // to return here after sign-in
  return (
    <SignInScreen
      notice={
        // A failed SSO redirect never reaches the auth machine, so nothing else
        // would explain why the user is back here.
        oauthStatus === "failed"
          ? "Single sign-on didn’t complete. Please try again."
          : undefined
      }
    />
  );
}
